import { t } from "@/lib/i18n/interpolate";
import {
  asClause,
  properWords,
  stripEnd,
  tidy,
  tidySeams,
} from "@/lib/tool-kit/essay/sentence";

export type ClosingType = "call-to-action" | "implication" | "future";

export interface ConclusionFrames {
  closing: Record<ClosingType, string>;
  /** Uses `{points}`: the main points joined as a list. */
  points: string;
  /** Uses `{thesis}`: the thesis as a clause. */
  restate: string;
}

export interface ConclusionInput {
  closing: ClosingType;
  points: string[];
  thesis: string;
}

const ENGLISH = /^en(?:-|$)/i;

function listOf(items: string[], locale: string): string {
  try {
    return new Intl.ListFormat(locale, {
      style: "long",
      type: "conjunction",
    }).format(items);
  } catch {
    return items.join(", ");
  }
}

/**
 * Builds the three-sentence conclusion: the thesis restated inside a fresh frame ("This
 * essay has argued that …"), one sentence that gathers the main points, and a closing
 * frame with a bracketed blank. The tool cannot paraphrase, so the wording of the thesis
 * is the writer's own; only the sentence around it is new. In English the first letter of
 * the thesis and points is lowered unless it reads as a name; other locales keep the
 * text as typed.
 */
export function buildConclusion(
  input: ConclusionInput,
  frames: ConclusionFrames,
  locale: string
): string {
  const proper = properWords([input.thesis, ...input.points]);
  const clause = (text: string) =>
    ENGLISH.test(locale) ? asClause(text, proper) : stripEnd(tidy(text));
  const sentences: string[] = [];
  if (input.thesis.trim().length > 0) {
    sentences.push(t(frames.restate, { thesis: clause(input.thesis) }));
  }
  if (input.points.length > 0) {
    sentences.push(
      t(frames.points, { points: listOf(input.points.map(clause), locale) })
    );
  }
  sentences.push(frames.closing[input.closing]);
  return sentences.map(tidySeams).join(" ");
}
